import { ChangeDetectorRef, Component, ElementRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { LL } from 'i18n/i18n.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { AlertController } from 'providers/alert-controller';
import { FitnessPlan, WelcomePhase } from '@cf-shared/data-model/fitness-plan';
import { UserDb } from 'services/user-db.service';
import { DatabaseWrapper , DocWatcher } from 'services/database-wrapper';
import { MenuController } from 'providers/menu-controller';
import { TranslatedString } from 'models/translated-string';
import { IIntakeFormPage } from 'pages/intake-form/intake-form-interface';
import { IHomePage, HomeTab } from 'pages/home/home-interface';

interface Slide {
  title: TranslatedString;
  text: TranslatedString;
  imageUrl: string;
};

@Component({
  selector: 'page-intro-carousel',
  templateUrl: 'intro-carousel-page.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IntroCarouselPage extends CsPage<{}> {
  protected LL = LL;
  protected hasClientSmallScreen = hasClientSmallScreen;
  @ViewChild('cardHolder') cardHolder: ElementRef;

  paramForce = false;  // set if the navparam contains "force"

  currentSlideNum = 0;

  slides: Slide[] = [];

  fitnessPlan: FitnessPlan;
  private docWatcher: DocWatcher;

  constructor(
    public navCtrl: NavController,
    public alertCtrl: AlertController,
    protected menuCtrl: MenuController,
    protected userDb: UserDb,
    protected changeDetector: ChangeDetectorRef,
  ) {
    super();
  }

  async ionViewCanEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.changeDetector.markForCheck();
      });
    }
    this.fitnessPlan = await this.userDb.loadPlanAsync(this.docWatcher);

    // Initialize slides with translations
    this.slides = [
      {
        title: LL.intro.slide1.title(),
        text: LL.intro.slide1.text(),
        imageUrl: 'assets/imgs/intro-01.jpg',
      },
      {
        title: LL.intro.slide2.title(),
        text: LL.intro.slide2.text(),
        imageUrl: 'assets/imgs/intro-02.jpg',
      },
      {
        title: LL.intro.slide3.title(),
        text: LL.intro.slide3.text(),
        imageUrl: 'assets/imgs/intro-03.jpg',
      },
      {
        title: LL.intro.slide4.title(),
        text: LL.intro.slide4.text(),
        imageUrl: 'assets/imgs/intro-04.jpg',
      },
    ];
  }

  async ionViewWillEnter() {
    this.menuCtrl.enable(false);
  }

  async ionViewWillLeave() {
    this.menuCtrl.enable(true);
  }

  async ionViewDidEnter() {
  }

  // true while a programmatic smooth-scroll (from Next/Back/Skip) is in
  // flight. suppresses the scroll listener so intermediate scrollLeft values
  // don't flip currentSlideNum back to the source index mid-animation
  private programmaticScrollPending = false;

  async ngAfterViewInit() {
    const scrollable = this.cardHolder.nativeElement as HTMLElement;

    // sync currentSlideNum (and therefore the dots + Next button label) live
    // as the user swipes. syncSlideIndexFromScroll is cheap (round + compare)
    // and short-circuits when the index hasn't changed, so listening to every
    // scroll event flips the dots the moment the new slide crosses the
    // viewport centerline rather than after the snap animation completes
    scrollable.addEventListener('scroll', this.handleCarouselScroll, { passive: true });
    scrollable.addEventListener('scrollend', this.handleCarouselScrollEnd, { passive: true });
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
    const el = this.cardHolder?.nativeElement;
    el?.removeEventListener('scroll', this.handleCarouselScroll);
    el?.removeEventListener('scrollend', this.handleCarouselScrollEnd);
  }

  private handleCarouselScroll = () => {
    if (this.programmaticScrollPending) return;
    this.syncSlideIndexFromScroll();
  };

  private handleCarouselScrollEnd = () => {
    this.programmaticScrollPending = false;
  };

  private syncSlideIndexFromScroll() {
    // scroll events can still fire after the page starts tearing down (before
    // ngOnDestroy detaches the listener), so guard markForCheck per CLAUDE.md
    if (this.viewDestroyed) return;
    const scrollable = this.cardHolder.nativeElement as HTMLElement;
    const slideWidth = scrollable.clientWidth;
    if (slideWidth === 0) return;
    const index = Math.round(scrollable.scrollLeft / slideWidth);
    if (index < 0 || index >= this.slides.length || index === this.currentSlideNum) return;
    this.currentSlideNum = index;
    this.changeDetector.markForCheck();
  }

  async closeCarousel() {
    if (this.fitnessPlan && this.fitnessPlan.welcomePhase === WelcomePhase.Intro) {
      this.fitnessPlan.welcomePhase = WelcomePhase.Intake;
      await this.userDb.savePlanAsync(DatabaseWrapper.getTransactionId());
      void this.navCtrl.setRootAsync(IIntakeFormPage, {});
    } else {
      void this.navCtrl.setRootAsync(IHomePage, { tab: HomeTab.DailyLog });
    }
  }

  nextSlide() {
    // if we're on the last content slide, Next closes the carousel
    if (this.currentSlideNum >= this.slides.length - 1) {
      void this.closeCarousel();
      return;
    }
    this.currentSlideNum = this.currentSlideNum + 1;
    this.scrollToCurrentImage();
  }

  prevSlide() {
    if (this.currentSlideNum <= 0) return;
    this.currentSlideNum = this.currentSlideNum - 1;
    this.scrollToCurrentImage();
  }

  private scrollToCurrentImage() {
    const scrollable = this.cardHolder.nativeElement as HTMLElement;
    const slideWidth = scrollable.clientWidth;
    // suppress the scroll listener for the duration of this animated scroll
    // so intermediate scrollLeft values don't flip currentSlideNum back to
    // the source index. cleared by scrollend when the snap settles — plus a
    // safeTimeout fallback for iOS 15-17.1 where scrollend never fires (the
    // event shipped in Safari/WebKit 17.2). 600ms covers the smooth-scroll
    // animation comfortably without trapping legitimate user swipes that
    // start the moment a button-tap-driven scroll completes.
    this.programmaticScrollPending = true;
    this.safeTimeout(() => this.programmaticScrollPending = false, 600);
    scrollable.scrollTo({
      left: this.currentSlideNum * slideWidth,
      behavior: 'smooth',
    });
    this.changeDetector.markForCheck();
  }

}
